import { useEffect, useState } from "react";
import { ApiError, api } from "@/lib/api";
import { parseImeis } from "@/lib/imei";
import AvisoVentasImeis from "@/components/AvisoVentasImeis";
import { verificarVentas, type VerificacionVenta } from "@/lib/ventas";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

/**
 * Modal para agregar uno o varios IMEIs a un caso. NO pide modelo: el equipo
 * se asume del modelo del caso (si es otro modelo, se abre un caso nuevo).
 * Lo abre el botón "Agregar IMEI" del tab y el atajo "+ Agregar IMEI" del
 * encabezado del caso.
 */
export default function AgregarImeiModal({
  open,
  onOpenChange,
  casoId,
  casoModelo,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  casoId: number;
  casoModelo: string | null;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [raw, setRaw] = useState("");
  const [guardando, setGuardando] = useState(false);
  // Aviso anti-goles mientras escribe (informativo, no bloquea).
  const [ventasCheck, setVentasCheck] = useState<VerificacionVenta[]>([]);

  useEffect(() => {
    const imeis = parseImeis(raw).filter((s) => s.length >= 6);
    if (imeis.length === 0) {
      setVentasCheck([]);
      return;
    }
    const t = setTimeout(() => {
      verificarVentas(imeis).then(setVentasCheck).catch(() => setVentasCheck([]));
    }, 500);
    return () => clearTimeout(t);
  }, [raw]);

  async function agregar() {
    const lista = parseImeis(raw);
    if (lista.length === 0) {
      toast.error("Escribe al menos un IMEI");
      return;
    }
    setGuardando(true);
    try {
      await api.post(`/api/casos/${casoId}/imeis`, {
        imeis: lista.map((imei) => ({ imei })),
      });
      toast.success(
        lista.length === 1 ? "IMEI agregado" : `${lista.length} IMEIs agregados`,
      );
      setRaw("");
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error("No se pudo agregar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setGuardando(false);
    }
  }

  const cuantos = parseImeis(raw).length;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        if (!v) setRaw("");
        onOpenChange(v);
      }}
      title="Agregar IMEI"
      description={
        casoModelo
          ? `Se registran con el modelo del caso: ${casoModelo}.`
          : "Se registran en este caso."
      }
      size="md"
      footer={
        <>
          <button onClick={() => onOpenChange(false)} className="btn-ghost text-sm">
            Cancelar
          </button>
          <button
            onClick={() => void agregar()}
            disabled={guardando || cuantos === 0}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {guardando
              ? "Agregando…"
              : cuantos > 1
                ? `Agregar ${cuantos} IMEIs`
                : "Agregar IMEI"}
          </button>
        </>
      }
    >
      <div className="space-y-2">
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="IMEI(s) — separados por coma, espacio o salto de línea"
          rows={3}
          autoFocus
          className="input w-full font-mono text-sm"
        />
        <p className="text-xs text-zinc-500">
          Puedes pegar varios a la vez. El equipo se asume del{" "}
          <strong>modelo del caso{casoModelo ? ` (${casoModelo})` : ""}</strong>; si
          es otro modelo, abre un <strong>caso nuevo</strong>.
        </p>
        <AvisoVentasImeis verificaciones={ventasCheck} />
      </div>
    </Modal>
  );
}
